import { Box, Grid, FormControlLabel, Typography, gridClasses } from "@mui/material";
import MzAlert from "@markaaz/ui/MzAlert";
import I18n from "i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import MzInput from "@markaaz/ui/MzInputControls";
import MzButton from "@markaaz/ui/MzButton";
import MzSelect from "@markaaz/ui/MzSelect";
import MzCheckbox from "@markaaz/ui/MzCheckbox";
import MzLink from "@markaaz/ui/MzLink";
import { MzMenuItem } from "@markaaz/ui/MzMenuControls";
import SignUpFooter from "./Footer";
import AuthFooterLink from "commonComponents/AuthFooterLink";
import CaptchaBox from "commonComponents/CaptchaBox";
import restrictedEmails from "../../utils/restrictedEmails";

const sx = {
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    marginTop: "20px",
    alignItems: "center",
    h1: {
      color: "darkBlue.c100",
      fontSize: "24px",
      fontWeight: "600",
      lineHeight: "36px"
    }
  },

  gridContainer: {
    marginBottom: "20px",
    [`.${gridClasses.item}`]: {
      paddingTop: "40px",
      paddingLeft: "20px"
    }
  },

  content: {
    width: "700px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },

  input: {},

  header: {
    marginTop: "40px",
    color: "darkBlue.c100"
  },

  submit: {
    marginTop: "20px",
    marginBottom: "15px"
  },

  email: {
    color: "darkBlue.c70"
  },

  username: {
    display: "none"
  },

  signin: {
    color: "darkBlue.c70",
    marginBottom: "50px"
  },

  footer: {
    display: "flex",
    width: "70%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  }
};

const validationSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  companyEmail: yup
    .string()
    .email(I18n.t("authPages.validEmailAddress"))
    .required("Email is required")
    .matches(/[a-z0-9]+@[a-z0-9]+\.[a-z]{2,3}/, I18n.t("authPages.validEmailAddress"))
    .test(
      "email-domain",
      "Please enter a business email address",
      (value) => !restrictedEmails.some((e) => value.includes(`@${e}`))
    ),
  companyName: yup.string().required("Company name is required"),
  phoneNumber: yup.string(),
  userRole: yup.string(),
  password: yup
    .string()
    .required("Password is required")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, I18n.t("authPages.passwordCriteria")),
  confirmPassword: yup
    .string()
    .required("Please re-enter your password")
    .oneOf([yup.ref("password"), null], "Passwords must match")
});

const roles = ["Product", "Technology", "Marketing", "Finance", "Sales", "Compliance", "Executive Management", "Other"];

export default function Register({
  isCaptchaSuccess,
  onCaptchaSuccess,
  onSubmit,
  onSignIn,
  showNotification,
  onAlertClose
}) {
  const initialValues = {
    firstName: "",
    lastName: "",
    companyEmail: "",
    username: "",
    companyName: "",
    phoneNumber: "",
    userRole: "",
    password: "",
    confirmPassword: "",
    isMarketingEmailEnabled: true,
    userType: "D"
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async () => {
      await onSubmit(formik.values);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true
  });

  return (
    <Box sx={sx.root}>
      <Box sx={sx.content} component="form">
        <Typography variant="h1" sx={sx.header}>
          {I18n.t("authPages.letsCreateTrialAccount")}
        </Typography>
        <Grid container sx={sx.gridContainer}>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="firstName"
              label="First Name"
              autoComplete="off"
              noBorder
              fullWidth
              required
              error={formik.touched.firstName && !!formik.errors.firstName}
              helperText={formik.touched.firstName && formik.errors.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="lastName"
              label="Last Name"
              autoComplete="off"
              noBorder
              fullWidth
              required
              error={formik.touched.lastName && !!formik.errors.lastName}
              helperText={formik.touched.lastName && formik.errors.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="companyEmail"
              label="Company Email"
              autoComplete="off"
              type="email"
              noBorder
              fullWidth
              required
              error={formik.touched.companyEmail && !!formik.errors.companyEmail}
              helperText={formik.touched.companyEmail && formik.errors.companyEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <MzInput
              sx={sx.username}
              tabIndex={-1}
              aria-hidden={true}
              name="username"
              autoComplete="off"
              noBorder
              fullWidth
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="companyName"
              label="Company Name"
              noBorder
              fullWidth
              required
              error={formik.touched.companyName && !!formik.errors.companyName}
              helperText={formik.touched.companyName && formik.errors.companyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="phoneNumber"
              label="Phone Number"
              noBorder
              fullWidth
              error={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
              helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <MzSelect
              label={I18n.t("authPages.roleInCompany")}
              name="userRole"
              fullWidth
              noBorder
              value={formik.values.userRole}
              error={formik.touched.userRole && !!formik.errors.userRole}
              helperText={formik.touched.userRole && formik.errors.userRole}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {roles.map((val) => (
                <MzMenuItem key={val} value={val}>
                  {val}
                </MzMenuItem>
              ))}
            </MzSelect>
          </Grid>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="password"
              label="Create a Password"
              autoComplete="new-password"
              type="password"
              noBorder
              fullWidth
              required
              error={formik.touched.password && !!formik.errors.password}
              helperText={(formik.touched.password && formik.errors.password) || I18n.t("authPages.passwordCriteria")}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <MzInput
              sx={sx.input}
              name="confirmPassword"
              label="Confirm Your Password"
              type="password"
              autoComplete="new-password"
              noBorder
              fullWidth
              required
              error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>

        <FormControlLabel
          sx={sx.email}
          control={
            <MzCheckbox
              name="isMarketingEmailEnabled"
              checked={formik.values.isMarketingEmailEnabled}
              onChange={formik.handleChange}
            />
          }
          label={
            <Typography variant="body2" color="darkBlue.c100">
              {I18n.t("authPages.marketingEmails")}
            </Typography>
          }
        />

        <CaptchaBox onSuccess={onCaptchaSuccess} />

        <MzButton
          sx={sx.submit}
          variant="contained"
          size="large"
          disabled={!isCaptchaSuccess || !formik.isValid || formik.isSubmitting}
          loading={formik.isSubmitting}
          onClick={formik.handleSubmit}
        >
          Continue
        </MzButton>

        <Typography variant="caption" sx={sx.signin}>
          {I18n.t("authPages.alreadyHaveAccount")}&nbsp;
          <MzLink href="#" onClick={onSignIn}>
            {I18n.t("authPages.signIn")}
          </MzLink>
        </Typography>
      </Box>
      <Box mb={10}>
        {showNotification.message && (
          <MzAlert
            sx={{ alignItems: "center" }}
            severity={showNotification.type === "success" ? "success" : "error"}
            onClose={onAlertClose}
          >
            {showNotification.message}
          </MzAlert>
        )}
      </Box>
      <Box sx={sx.footer}>
        <SignUpFooter />
        <AuthFooterLink />
      </Box>
    </Box>
  );
}
